import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { TabPanel } from '../TabPanel/TabPanel'
import { TLShape, track } from '@tldraw/tldraw'
import { Position } from './components/Position'
import Styles from './ShapesTab.module.scss'
import { FontToolsCard } from './components/FontToolsCard'
import { ColorToolsCard } from './components/ColorToolsCard'
import { Button } from 'blaze-ui/Button'
import { ArrowRefresh2, Edit1, Eraser, Font, NormalText, Trash2 } from 'blaze-ui/icons'
import { TextBoxShape } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/Types'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { GeoShape } from 'components/VisualStudioPlayground/Shapes/GeoShape/Types'
import { camelCase, startCase } from 'lodash'
import { TRANSPARENT_COLOR } from '../ColorsPanel/constants'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ReplaceButton } from './components/ReplaceButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { MobileButton } from '../MobileButton/MobileButton'
import { MobileBackgroundButton } from './components/MobileBackgroundButton'
import { TextEditingDrawer } from '../TextEditingDrawer/TextEditingDrawer'
import { BackgroundsDrawer } from '../BackgroundsTab/BackgroundsDrawer/BackgroundsDrawer'
import { MobileFillCanvasButton } from './components/MobileFillCanvasButton'

export const shapeName = ({ shapes, useTextContent = false }: { shapes: TLShape[]; useTextContent?: boolean }) => {
  if (shapes.length === 1) {
    const shape = shapes[0]
    switch (shape.type) {
      case 'textBox':
        if (!useTextContent) return 'Text'
        return (shape as TextBoxShape).props.text
      case 'image':
        const imageShape = shape as ImageShape
        return imageShape.meta.colorSet ? 'Graphic' : 'Image'
      case 'frame':
        return 'Background'
      case 'video':
        return 'Video'
      case 'group':
        return 'Group'
      default:
        const geoShape = shape as GeoShape
        return `${startCase(camelCase(geoShape.props.geo))} Shape`
    }
  }
  return `${shapes.length} Shapes`
}

const ShapesTab = track(
  observer(() => {
    const editor = useEditor()
    const selectedShapes = editor.getSelectedShapes()
    const selectedShape = editor.getOnlySelectedShape()
    const { designStore, drawersStore } = useRootStore()
    const { isMobile } = useResponsiveDesign()

    if (!editor || selectedShapes.length === 0) return null

    const title = `${shapeName({ shapes: selectedShapes })}${selectedShapes.length > 1 ? ' Selected' : ''}`
    const isBackground = selectedShape?.type === 'frame'

    const eraseBackground = () => {
      if (!selectedShape) return

      if (selectedShape.type === 'frame') {
        editor.updateShape({
          ...selectedShape,
          props: {
            fill: TRANSPARENT_COLOR,
          },
        })
      }

      drawersStore.closeDrawer()
    }

    const deleteShapes = () => {
      editor.deleteShapes(selectedShapes)
      drawersStore.closeDrawer()
    }

    return (
      <TabPanel.Root>
        <TabPanel.Header title={title} onBack={() => designStore?.openDefaultTab()} />
        <TabPanel.Body>
          <div className={Styles.container}>
            {selectedShape && (
              <>
                {(!isMobile || selectedShape.type === 'mask') && <ReplaceButton selectedShape={selectedShape} />}
                {isMobile && selectedShape.type === 'image' && (
                  <div className={Styles.topSection}>
                    <div className={Styles.imageButtons}>
                      <ReplaceButton selectedShape={selectedShape} />
                      <MobileBackgroundButton editor={editor} selectedShape={selectedShape as ImageShape} />
                      <MobileFillCanvasButton editor={editor} selectedShape={selectedShape as ImageShape} />
                    </div>
                  </div>
                )}
                {isMobile && selectedShape.type === 'textBox' && (
                  <div className={Styles.topSection}>
                    <div className={Styles.textButtons}>
                      <MobileButton
                        icon={Edit1}
                        onPress={() => {
                          drawersStore.openDrawer(TextEditingDrawer, {
                            shapeProps: (selectedShape as TextBoxShape).props,
                            onConfirm: (newProps) => {
                              editor.updateShape({
                                ...selectedShape,
                                props: newProps,
                              })
                            },
                          })
                        }}
                      >
                        Edit Text
                      </MobileButton>
                      <MobileButton icon={Font} onPress={() => designStore?.openPanel({ type: 'textStyles' })}>
                        Change Style
                      </MobileButton>
                      <MobileButton icon={NormalText} onPress={() => designStore?.openPanel({ type: 'fontFamily' })}>
                        Select Font
                      </MobileButton>
                    </div>
                  </div>
                )}
                <div className={Styles.cards}>
                  {isBackground && (
                    <Button
                      size={isMobile ? 'lg' : 'md'}
                      variant="tertiary"
                      fullWidth={isMobile}
                      onPress={() => {
                        if (isMobile) {
                          drawersStore.closeDrawer()
                          editor.selectNone()
                          drawersStore.openDrawer(BackgroundsDrawer)
                        } else {
                          designStore?.openTab('backgrounds')
                        }
                      }}
                      frontIcon={ArrowRefresh2}
                    >
                      Change Background
                    </Button>
                  )}
                  {selectedShape.type === 'textBox' && !isMobile && <FontToolsCard />}
                  <ColorToolsCard />
                </div>
              </>
            )}
            <Position />
            {isBackground ? (
              <Button size={isMobile ? 'lg' : 'md'} variant="secondary" onPress={eraseBackground} frontIcon={Eraser}>
                Erase
              </Button>
            ) : (
              <Button size={isMobile ? 'lg' : 'md'} variant="secondary" onPress={deleteShapes} frontIcon={Trash2}>
                Delete {shapeName({ shapes: selectedShapes })}
              </Button>
            )}
          </div>
        </TabPanel.Body>
      </TabPanel.Root>
    )
  }),
)

export { ShapesTab }
